import { ProfileRoles } from '@bp/pim-auth-constants';
import { Grid, GridColumn, GridRow, ModalBottomButtons, Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import type { ImportPersonsFormDataQuery } from 'client/gql/graphql';
import { Formik } from 'formik';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import type { SingleValue } from 'react-select';
import { useQuery } from 'urql';
import { organizationRolesAsString } from 'utils/organizationRolesAsString';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { findMatchingProfiles } from '../../../utils/findMatchingProfiles';
import type { ProcuratDepartmentPersonsTableData } from '../tables/ProcuratDepartmentPersonsTable';
import { AssignExistingProfile } from './AssignExistingProfile';

const ImportPersonsFormQueryDocument = graphql(`
  query ImportPersonsFormData($where: ProfileWhere!) {
    profiles(where: $where) {
      uuid
      __typename
      organizationRoles
      firstName
      lastName
      email
      selectName
      foreignRefId
    }
  }
`);

export type ProfileQueryType = Pick<ImportPersonsFormDataQuery, 'profiles'>['profiles'][0];

export type GrantRoleValues = Record<string, { role: ProfileRoles; assign?: string }>;

type ImportPersonsFormProps = {
  personsData: ProcuratDepartmentPersonsTableData[];
  onImport: (x: GrantRoleValues) => Promise<void> | void;
  onClose: () => void;
};

export const ImportPersonsForm = ({ personsData, onImport, onClose }: ImportPersonsFormProps) => {
  const { t } = useTranslation();

  const { organization } = useContext(OrganizationConfigContext);

  const [{ data }, reFetchBpProfiles] = useQuery({
    query: ImportPersonsFormQueryDocument,
    variables: {
      where: {
        AND: [{ organization: { uuid: organization?.uuid } }],
      },
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies(personsData): refetch on personsData change
  useEffect(() => {
    reFetchBpProfiles({ requestPolicy: 'network-only' });
  }, [personsData, reFetchBpProfiles]);

  // combine incoming data with matching profiles
  const combinedData = personsData.map((person) => {
    return {
      ...person,
      matching: findMatchingProfiles(data?.profiles, person) ?? [],
    };
  });

  const dataWithMatching = combinedData.filter((item) => item.matching.length > 0).sort();
  const dataWithoutMatching = combinedData.filter((item) => item.matching.length === 0).sort();

  const initialValues: GrantRoleValues = {};
  for (const person of personsData) {
    initialValues[person.personId.toString()] = {
      role: (person.roles[0] as ProfileRoles) ?? ProfileRoles.Other,
    };
  }

  const selectOpts: SelectOptionType[] = [
    ProfileRoles.Other,
    ProfileRoles.Student,
    ProfileRoles.Teacher,
    ProfileRoles.Parent,
  ].map((r) => ({
    label: organizationRolesAsString([r]),
    value: r,
  }));

  return (
    <Formik<GrantRoleValues>
      initialValues={initialValues}
      onSubmit={() => {
        // no-op
      }}
    >
      {({ setFieldValue, values, isSubmitting, errors, resetForm }) => (
        <Form>
          <Grid>
            <GridRow>
              <GridColumn width={6}>&nbsp;</GridColumn>
              <GridColumn width={2}>
                <div>{t('procuratImport.chooseRole')}</div>
                <Select
                  options={selectOpts}
                  isSearchable={true}
                  onChange={async (event) => {
                    const role = (event as SingleValue<SelectOptionType>)?.value as ProfileRoles;
                    await Promise.all(
                      personsData.map((person) =>
                        setFieldValue(person.personId.toString(), {
                          ...values[person.personId],
                          role,
                        }),
                      ),
                    );
                  }}
                  name={'roleSelect'}
                  dense
                  placeholder={t('procuratImport.roleForAll')}
                />
              </GridColumn>
              <GridColumn width={3}>&nbsp;</GridColumn>
            </GridRow>
            <GridRow spacingTop='none'>
              <GridColumn width={3}>&nbsp;</GridColumn>
              <GridColumn width={3}> {t('procuratImport.previouslyImportedWithRole')}</GridColumn>
              <GridColumn width={2}>&nbsp;</GridColumn>
              <GridColumn width={3} align={'start'} justify={'center'}>
                {t('procuratImport.assignExistingProfile')}
              </GridColumn>
            </GridRow>
            {dataWithMatching.map((person) => (
              <GridRow spacingTop='none' key={person.personId}>
                <GridColumn width={3} align={'start'} justify={'center'}>
                  {person.firstName} {person.lastName}
                </GridColumn>
                <GridColumn width={3}> &nbsp;</GridColumn>
                <GridColumn width={2} align={'start'} justify={'center'}>
                  <Select
                    value={selectOpts.find((v) => values[person.personId].role.includes(v.value as string))}
                    options={selectOpts}
                    isSearchable={true}
                    onChange={async (event) => {
                      const role = (event as SingleValue<SelectOptionType>)?.value as ProfileRoles;
                      await setFieldValue(person.personId.toString(), {
                        ...values[person.personId],
                        role,
                      });
                    }}
                    name={'roleSelect'}
                    dense
                  />
                </GridColumn>
                <GridColumn width={3} align={'start'} justify={'center'}>
                  <AssignExistingProfile
                    matching={person.matching}
                    handleSelect={(uuid) =>
                      setFieldValue(person.personId.toString(), {
                        ...values[person.personId],
                        assign: uuid,
                      })
                    }
                  />
                </GridColumn>
              </GridRow>
            ))}
          </Grid>
          <hr />
          <Grid>
            {dataWithoutMatching.map((person) => {
              return (
                <GridRow spacingTop='none' key={person.personId}>
                  <GridColumn width={3} align={'start'} justify={'center'}>
                    {person.firstName} {person.lastName}
                  </GridColumn>
                  <GridColumn width={3} align={'start'} justify={'center'}>
                    {selectOpts.find((v) => person.roles.includes(v.value as string))?.label}
                  </GridColumn>
                  <GridColumn width={2} align={'start'} justify={'center'}>
                    <Select
                      value={selectOpts.find((v) => values[person.personId].role.includes(v.value as string))}
                      options={selectOpts}
                      isSearchable={true}
                      onChange={async (event) => {
                        const role = (event as SingleValue<SelectOptionType>)?.value as ProfileRoles;
                        await setFieldValue(person.personId.toString(), {
                          ...values[person.personId],
                          role,
                        });
                      }}
                      name={'roleSelect'}
                      dense
                    />
                  </GridColumn>
                  <GridColumn width={3}>&nbsp;</GridColumn>
                </GridRow>
              );
            })}
          </Grid>

          <ModalBottomButtons
            closeButton={{
              callback: async () => {
                onClose();
                resetForm();
              },
            }}
            submitButton={{
              text: t('procuratImport.importPersons'),
              callback: async (_e) => {
                onClose();
                await onImport(values);
                resetForm();
              },
            }}
            isLoading={isSubmitting}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
};
